import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {Label} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {BaseItemPickerProps} from '@github-ui/item-picker-base/base-item-picker-props'

import {assigneesGroup, getGroupItemId, sortAssigneePickerUsers, suggestionsGroup} from './utils'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import styles from './AssigneePickerBase.module.css'

type Name = 'assignee' | 'author'

export const DEFAULT_MAXIMUM_ASSIGNEES = 10

const AGENT_DESCRIPTIONS: Record<string, string> = {
  Copilot: 'GitHub',
  Codex: 'OpenAI',
  Claude: 'Anthropic',
}

export type AssigneePickerBaseAssignee = {
  id: string
  login: string
  name: string | undefined | null
  avatarUrl: string
  __typename: string
  isCopilot?: boolean
  isAgent?: boolean
  displayName?: string | null | undefined
  profileResourcePath?: string | null | undefined
}
type TAssigneeType = AssigneePickerBaseAssignee

export type AssigneePickerBaseProps = Omit<BaseItemPickerProps<TAssigneeType>, 'filterItems'> & {
  assignees?: TAssigneeType[]
  /** An optional data set to be used when the picker is first opened. If this data is not provided, a loading state
   * is shown in the picker and the data is loaded on demand.
   */
  suggestions?: TAssigneeType[]
  noAssigneeOption?: ExtendedItemProps<TAssigneeType> & {selected?: boolean}
  name?: Name
  maximumAssignees?: number
  showNoMatchItem?: boolean
  responsiveOnNarrow?: boolean
}

export type AssigneePickerInternalProps = {
  searchAssignees: (query: string) => Promise<TAssigneeType[]> | TAssigneeType[]
  viewer?: TAssigneeType | null
}

const DEFAULT_ASSIGNEES: TAssigneeType[] = []
const DEFAULT_SUGGESTIONS: TAssigneeType[] = []
export function AssigneePickerBase({
  assignees = DEFAULT_ASSIGNEES,
  suggestions = DEFAULT_SUGGESTIONS,
  searchAssignees,
  onSelectionChange,
  insidePortal,
  anchorElement,
  nested,
  isLoading = false,
  noAssigneeOption = undefined,
  triggerOpen,
  selectionVariant = 'multiple',
  title,
  maximumAssignees = DEFAULT_MAXIMUM_ASSIGNEES,
  name = 'assignee',
  showNoMatchItem = false,
  viewer,
  anchorSide,
  responsiveOnNarrow = false,
}: AssigneePickerBaseProps & AssigneePickerInternalProps) {
  const [filter, setFilter] = useState('')
  const [searchResults, setSearchResults] = useState<TAssigneeType[] | undefined>(undefined)
  const [currentlySelectedAssignees, setCurrentlySelectedAssignees] = useState<TAssigneeType[]>(assignees)
  const filterRef = useRef(filter)
  const {addToast} = useToastContext()
  const responsiveOnNarrowEnabled =
    responsiveOnNarrow || isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentlySelectedAssignees(assignees)
  }, [assignees])

  const getItemKey = useCallback((assignee: TAssigneeType) => assignee.login, [])

  const items = useMemo(() => {
    let sortedItems = []
    if (searchResults) {
      sortedItems = sortAssigneePickerUsers(assignees, searchResults, filter)
    } else {
      sortedItems = sortAssigneePickerUsers(
        assignees,
        assignees.concat(
          suggestions.filter(suggestion => !assignees.find(assignee => assignee.login === suggestion.login)),
        ),
        filter,
      )
    }

    if (viewer) {
      const currentUserIndex = sortedItems.findIndex(item => item.login === viewer.login)
      if (currentUserIndex !== -1) {
        // Remove the current user from its current position in the list to avoid duplicates
        sortedItems.splice(currentUserIndex, 1)
      } else if (searchResults) {
        // If the current user is not in the search results, we don't want to show it in the suggestions
        return sortedItems
      }
      // Add the current user to the top of the list
      sortedItems.unshift(viewer)
    }

    if (
      noAssigneeOption &&
      !noAssigneeOption.selected &&
      !sortedItems.find(i => i.id === SPECIAL_VALUES.noAssigneeData.id)
    ) {
      sortedItems.unshift(SPECIAL_VALUES.noAssigneeData as TAssigneeType)
    }

    return sortedItems
  }, [searchResults, viewer, noAssigneeOption, assignees, filter, suggestions])

  const groupItemId = useCallback((assignee: TAssigneeType) => getGroupItemId(assignees, assignee), [assignees])

  const showMaxLimitWarning = useMemo(
    () => !!(maximumAssignees && currentlySelectedAssignees.length >= maximumAssignees),
    [currentlySelectedAssignees.length, maximumAssignees],
  )

  const convertToItemProps = useCallback(
    (assignee: TAssigneeType): ExtendedItemProps<TAssigneeType> => {
      const isCurrentlySelected = currentlySelectedAssignees.some(selected => selected.id === assignee.id)
      const shouldDisable = showMaxLimitWarning && !isCurrentlySelected

      if (noAssigneeOption && assignee.id === SPECIAL_VALUES.noAssigneeData.id) {
        const noAssigneeItem = {...noAssigneeOption}
        noAssigneeItem.groupId = groupItemId(assignee)
        noAssigneeItem.disabled = shouldDisable
        return noAssigneeItem
      }
      if (showNoMatchItem && assignee.id === SPECIAL_VALUES.noMatchData.id) {
        return {
          ...SPECIAL_VALUES.noMatchData,
          groupId: groupItemId(assignee),
          source: assignee,
          text: `${name}:${filter}`,
          className: styles.Item,
          disabled: shouldDisable,
        }
      }
      const assigneeIsCopilot = assignee.isCopilot
      const assigneeIsBot = assignee.__typename === 'Bot'
      const showAiBadge = assigneeIsCopilot || assignee.isAgent
      // For non-bots we prefer to show the login as the display name
      // As the User.to_display_login method in the backend does some special EMU handling
      const assigneeDisplayName = assigneeIsCopilot
        ? LABELS.copilotDisplayName
        : assigneeIsBot && assignee.displayName
          ? assignee.displayName
          : assignee.login

      const assigneeDescription =
        (assigneeIsCopilot || assignee.isAgent) && assignee.displayName
          ? (AGENT_DESCRIPTIONS[assignee.displayName] ?? '')
          : assignee.name || ''

      return {
        id: assignee.id,
        text: assigneeDisplayName,
        description: assigneeDescription,
        source: assignee,
        groupId: groupItemId(assignee),
        leadingVisual: () =>
          assigneeIsCopilot ? (
            <CopilotAvatar />
          ) : assignee.avatarUrl.length === 0 ? null : (
            <GitHubAvatar alt="" src={assignee.avatarUrl} />
          ),
        trailingVisual: () => {
          if (showAiBadge) {
            return <Label>AI</Label>
          }

          if (assigneeIsBot) {
            return <Label>bot</Label>
          }

          return null
        },
        className: styles.Item,
        disabled: shouldDisable,
      }
    },
    [filter, groupItemId, name, noAssigneeOption, showNoMatchItem, showMaxLimitWarning, currentlySelectedAssignees],
  )

  const onClose = useCallback(() => {
    setSearchResults(undefined)
  }, [])

  const fetchSearchData = useCallback(
    async (query: string) => {
      if (query === '') {
        setSearchResults(undefined)
        return
      }

      try {
        const suggestedAssignees = await searchAssignees(query)
        const currentFilter = filterRef.current
        if (query.trim() === currentFilter.trim()) {
          setSearchResults(suggestedAssignees)
        }
      } catch {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotSearchAssignees,
        })
      }
    },
    [addToast, searchAssignees],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
    {
      leading: true,
      trailing: true,
      onChangeBehavior: 'flush',
    },
  )

  const filterItems = useCallback(
    (value: string) => {
      const trimmedFilter = value.trim()
      if (filter === trimmedFilter) return

      debounceFetchSearchData(trimmedFilter)
      setFilter(value)
      filterRef.current = trimmedFilter
    },
    [debounceFetchSearchData, filter],
  )

  const onLiveSelectionChange = useCallback(
    (selectedAssignees: TAssigneeType[]) => {
      const visibleItemIds = new Set(items.map(item => item.id))
      const selectedItemIds = new Set(selectedAssignees.map(assignee => assignee.id))

      // Keep previously selected items that are now hidden by the filter
      const hiddenSelectedItems = currentlySelectedAssignees.filter(
        assignee => !visibleItemIds.has(assignee.id) && !selectedItemIds.has(assignee.id),
      )

      setCurrentlySelectedAssignees([...selectedAssignees, ...hiddenSelectedItems])
    },
    [currentlySelectedAssignees, items],
  )

  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []

    // Find items that are assignees and suggestions (non-assignees are suggestion items)
    const assigneeItems = items.filter(i => assignees.find(a => a.id === i.id))
    const suggestionItems = items.filter(i => !assignees.find(a => a.id === i.id))

    if (assigneeItems.length > 0) {
      itemGroups.push(assigneesGroup)
    }
    if (suggestionItems.length > 0) {
      itemGroups.push(suggestionsGroup)
    }
    return itemGroups
  }, [assignees, items])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const showNoMatchMessage = useMemo(() => !showNoMatchItem && items.length === 0, [showNoMatchItem, items.length])
  return (
    <div>
      <ItemPicker
        items={items}
        initialSelectedItems={assignees}
        loading={isLoading}
        filterItems={filterItems}
        title={title || LABELS.assigneesHeader}
        getItemKey={getItemKey}
        triggerOpen={triggerOpen}
        convertToItemProps={convertToItemProps}
        placeholderText={LABELS.filterUsers(name)}
        selectionVariant={maximumAssignees === 1 ? 'single' : selectionVariant || 'multiple'}
        onSelectionChange={onSelectionChange}
        onLiveSelectionChange={onLiveSelectionChange}
        onClose={onClose}
        renderAnchor={props => anchorElement(props, anchorRef)}
        groups={groups}
        insidePortal={insidePortal}
        height={'large'}
        width={'medium'}
        nested={nested}
        resultListAriaLabel={'User results'}
        selectPanelRef={anchorRef}
        anchorSide={anchorSide}
        notice={
          maximumAssignees && showMaxLimitWarning
            ? {
                text: LABELS.limitWarning(maximumAssignees),
                variant: 'warning',
              }
            : undefined
        }
        customNoMatchItem={
          showNoMatchItem
            ? ({
                id: SPECIAL_VALUES.noMatchData.id,
                avatarUrl: SPECIAL_VALUES.noMatchData.avatarUrl,
                login: filter,
              } as TAssigneeType)
            : undefined
        }
        improvedNoMatchAccessibility={showNoMatchMessage}
        noMatchMessage={
          showNoMatchMessage
            ? {
                title: 'No assignees were found',
                body: 'Try searching for a different name for results.',
                variant: 'empty',
              }
            : undefined
        }
        keybindingCommandId={getKeyBindingCommandId(name)}
        responsiveOnNarrow={responsiveOnNarrowEnabled}
      />
    </div>
  )
}

const getKeyBindingCommandId = (name?: string) =>
  name === 'author' ? 'item-pickers:open-author' : 'item-pickers:open-assignees'
